/**
 * Offline Observation Sync Worker (v1.1.29)
 * ==========================================
 * Drains the observation offline queue when the device comes online.
 *
 * For each queued observation, POSTs to /api/observations with the
 * `offline_observation_key` set — the server uses that key for idempotency
 * so retries from flaky connections never create duplicate observations.
 *
 * DESIGN INVARIANT (per product owner directive 2026-02-06):
 *   This worker MUST NOT modify any existing service. It composes a single
 *   axios POST verbatim. If this file is deleted, ALL three existing offline
 *   flows continue to work unchanged:
 *     - syncManager.js (legacy new-asset queue)
 *     - offlineExistingAssetSync.js (v1.1.25 add-photos queue)
 */
import axios from 'axios';
import { toast } from 'sonner';
import {
  getPendingObservations,
  updateObservationOpStatus,
  clearCompletedObservations,
} from './offlineObservationQueue';
// v1.3.16 — Respect the manual airplane toggle. When manual mode
// is on, `isEffectivelyOnline()` returns false so the drain gate
// below correctly holds observations locally until the user turns
// airplane mode off. Behavior in the two pre-existing modes is
// unchanged.
import { isEffectivelyOnline, subscribeConnectivity } from '../lib/connectivity';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

let isRunning = false;
let isInitialized = false;
const listeners = new Set();

export const subscribeObservationSync = (cb) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

// v1.2.20 — Public publisher so other modules (e.g. SiteReviewDialog
// bulk-save) can trigger the same "finished" event the offline drain
// emits, causing ObservationsSection to refetch. Strictly additive — no
// existing call path uses this.
export const publishObservationsChanged = () => notify({ type: 'finished' });

const notify = (event) => {
  listeners.forEach(cb => {
    try { cb(event); } catch (e) { /* listener failure must not stop sync */ }
  });
};

const drainOne = async (op) => {
  const token = localStorage.getItem('token');
  const authHeader = token ? { Authorization: `Bearer ${token}` } : undefined;

  try {
    const payload = {
      assessment_id: op.assessment_id,
      title: op.title,
      description: op.description || '',
      category: op.category || 'Other',
      urgency: op.urgency || 'Short-term',
      estimated_cost: Number(op.estimated_cost) || 0,
      cost_basis_notes: op.cost_basis_notes || undefined,
      recommendation: op.recommendation || undefined,
      floor_level: op.floor_level || undefined,
      room_number: op.room_number || undefined,
      photos_base64: Array.isArray(op.photos_base64) && op.photos_base64.length > 0
        ? op.photos_base64
        : undefined,
      // Idempotency: server returns the existing row instead of creating
      // a duplicate when the same key reappears.
      offline_observation_key: op.offline_observation_key,
    };
    const res = await axios.post(`${API}/observations`, payload, { headers: authHeader });
    if (res.status !== 200 && res.status !== 201) {
      throw new Error(`POST /observations returned ${res.status}`);
    }
    await updateObservationOpStatus(op.id, 'completed');
    return true;
  } catch (err) {
    const msg = err?.response?.data?.detail || err?.message || 'sync failed';
    await updateObservationOpStatus(op.id, 'failed', msg);
    return false;
  }
};

export const drainObservationQueue = async () => {
  if (isRunning) return;
  if (!isEffectivelyOnline()) return;
  isRunning = true;

  try {
    const ops = await getPendingObservations();
    if (ops.length === 0) {
      isRunning = false;
      return;
    }

    notify({ type: 'started', total: ops.length });

    let succeeded = 0;
    let failed = 0;
    for (const op of ops) {
      if ((op.retry_count || 0) >= 5) continue;
      try { await updateObservationOpStatus(op.id, 'syncing'); } catch (e) { /* non-fatal */ }
      const ok = await drainOne(op);
      if (ok) succeeded++; else failed++;
      notify({ type: 'progress', succeeded, failed, total: ops.length });
    }

    if (succeeded > 0) {
      toast.success(
        `Synced ${succeeded} facility observation${succeeded > 1 ? 's' : ''}`,
        { duration: 4500 }
      );
    }
    if (failed > 0) {
      toast.warning(
        `${failed} observation sync${failed > 1 ? 's' : ''} failed — will retry automatically`,
        { duration: 5500 }
      );
    }

    try { await clearCompletedObservations(); } catch (e) { /* non-fatal */ }
    notify({ type: 'finished', succeeded, failed });
  } finally {
    isRunning = false;
  }
};

export const initObservationSync = () => {
  if (isInitialized) return;
  isInitialized = true;

  // v1.3.16 — Drain on ANY connectivity change that lands us in
  // effective-online state. This subsumes the pre-existing
  // 'online' event listener and adds the manual-airplane-off
  // trigger, without duplicating drain logic.
  subscribeConnectivity(({ effectiveOnline }) => {
    if (effectiveOnline) {
      setTimeout(() => { drainObservationQueue(); }, 750);
    }
  });

  if (isEffectivelyOnline()) {
    setTimeout(() => { drainObservationQueue(); }, 2000);
  }
};
